import GIF from 'gif.js.optimized'
import { GifGenerationError } from './generation-error'

export const GIF_ENCODING_STALL_TIMEOUT_MS = 30_000

type GeneratedGif = File & {
    accepted: boolean
    lastModifiedDate: Date
    name: string
}

/**
 * gif.js only reports successful worker messages; it does not attach an
 * onerror handler to its workers. A failed worker can therefore leave the
 * encoder pending forever. Bound the wait and abort every active worker so
 * callers can show an error and leave their loading state.
 */
export const renderGif = (gif: GIF): Promise<File> =>
    new Promise((resolve, reject) => {
        let timedOut = false
        let timeoutId: ReturnType<typeof setTimeout>
        const abortStalledEncoding = () => {
            timedOut = true
            gif.abort()
        }
        const clearEncodingTimeout = () => clearTimeout(timeoutId)
        const resetEncodingTimeout = () => {
            clearEncodingTimeout()
            timeoutId = setTimeout(abortStalledEncoding, GIF_ENCODING_STALL_TIMEOUT_MS)
        }

        // Progress fires after each encoded frame. Resetting the watchdog lets
        // legitimately slow exports continue while still bounding a worker
        // that stops answering altogether.
        gif.on('progress', resetEncodingTimeout)
        resetEncodingTimeout()

        gif.once('finished', (blob) => {
            clearEncodingTimeout()
            const generatedGif = blob as GeneratedGif
            generatedGif.lastModifiedDate = new Date()
            generatedGif.name = 'giphy.gif'
            generatedGif.accepted = true
            resolve(generatedGif)
        })

        gif.once('abort', () => {
            clearEncodingTimeout()
            reject(
                timedOut
                    ? new GifGenerationError('encoding_timeout', 'GIF encoding did not finish before the timeout')
                    : new GifGenerationError('encoding_aborted', 'GIF encoding was aborted before completion')
            )
        })

        try {
            gif.render()
        } catch (error) {
            clearEncodingTimeout()
            reject(error)
        }
    })
